/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { PayPalNamespace, PayPalSdkConfig } from './types';

/**
 * Loads the PayPal JS SDK once and resolves the `paypal` global.
 *
 * Shared by the cart-express and checkout PayPal buttons (CART-16 / CHECKOUT-02). The SDK is
 * injected lazily (not on initial page load) to avoid blocking LCP; callers preconnect to
 * paypal.com via resource hints (config.server.ts links) so the fetch is warm. The returned
 * promise is memoized so multiple buttons on a page share one SDK load.
 *
 * Only the PUBLIC client-id is used here — no secret is ever referenced on the client.
 */

let sdkPromise: Promise<PayPalNamespace> | null = null;

/**
 * Builds the PayPal SDK script URL from config.
 *
 * @param config the public PayPal SDK config
 * @returns the fully-qualified SDK URL
 */
export function buildSdkUrl(config: PayPalSdkConfig): string {
    const params = new URLSearchParams({
        'client-id': config.clientId,
        currency: config.currency,
        intent: config.intent,
        components: 'buttons,messages,funding-eligibility',
    });

    if (config.enableFunding) {
        params.set('enable-funding', config.enableFunding);
    }

    return `${config.sdkBaseUrl.replace(/\/$/, '')}?${params.toString()}`;
}

/**
 * Loads the PayPal SDK and resolves the `paypal` namespace.
 *
 * @param config the public PayPal SDK config
 * @returns a promise resolving to the `paypal` global
 */
export function loadPayPalSdk(config: PayPalSdkConfig): Promise<PayPalNamespace> {
    if (typeof window === 'undefined') {
        return Promise.reject(new Error('loadPayPalSdk must run in the browser'));
    }

    if (window.paypal) {
        return Promise.resolve(window.paypal);
    }

    if (sdkPromise) {
        return sdkPromise;
    }

    if (!config.clientId) {
        return Promise.reject(new Error('PayPal SDK client-id is not configured'));
    }

    sdkPromise = new Promise<PayPalNamespace>((resolve, reject) => {
        const url = buildSdkUrl(config);
        const existing = document.querySelector<HTMLScriptElement>(`script[src="${url}"]`);

        const onReady = () => {
            if (window.paypal) {
                resolve(window.paypal);
            } else {
                sdkPromise = null;
                reject(new Error('PayPal SDK loaded but window.paypal is undefined'));
            }
        };

        if (existing) {
            existing.addEventListener('load', onReady, { once: true });
            existing.addEventListener(
                'error',
                () => {
                    sdkPromise = null;
                    reject(new Error('Failed to load PayPal SDK'));
                },
                { once: true }
            );
            return;
        }

        const script = document.createElement('script');
        script.src = url;
        script.async = true;
        script.onload = onReady;
        script.onerror = () => {
            sdkPromise = null;
            reject(new Error('Failed to load PayPal SDK'));
        };
        document.head.appendChild(script);
    });

    return sdkPromise;
}

/** Test-only: reset the memoized SDK promise. */
export function __resetPayPalSdkForTest(): void {
    sdkPromise = null;
}
